/**
 * deduplicates an array by identity
 * @param arr the array to deduplicate
 * @returns a new array with duplicate values removed
 */
export const unique = <T>(arr: T[]): T[] => {
  return Array.from(new Set(arr));
};

/**
 * typed Object.entries that preserves key-value pair types
 * @param obj the object to extract entries from
 * @returns an array of [key, value] tuples
 */
type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];
export const entries = <T extends object>(obj: T): Entries<T> => {
  // @ts-expect-error
  return Object.entries(obj);
};

// Workaround: extracting the check into a variable prevents TypeScript from
// inferring `Map` as `never` in the `new Map()` fallback.
const hasMapGroupBy = "groupBy" in Map;

/**
 * groups items into a Map keyed by the selector's return value
 * @param items the array to group
 * @param selector function to derive grouping keys
 * @returns a Map of keys to arrays of items
 */
export const groupBy = <K, T>(
  items: T[],
  selector: (item: T, index: number) => K
): Map<K, T[]> => {
  if (hasMapGroupBy) {
    return Map.groupBy(items, selector);
  }

  const map = new Map<K, T[]>();
  for (let idx = 0, len = items.length; idx < len; idx++) {
    const value = items[idx];
    const key = selector(value, idx);

    const array = map.get(key);
    if (array === undefined) {
      map.set(key, [value]);
    } else {
      array.push(value);
    }
  }

  return map;
};

/**
 * groups items into a Map, skipping items whose selector returns undefined
 * @param items the array to group
 * @param selector function to derive grouping keys
 * @returns a Map of keys to arrays of items
 */
export const groupByDefined = <K, T>(
  items: T[],
  selector: (item: T, index: number) => K | undefined
): Map<K, T[]> => {
  const map = new Map<K, T[]>();
  for (let idx = 0, len = items.length; idx < len; idx++) {
    const value = items[idx];
    const key = selector(value, idx);

    if (key === undefined) {
      continue;
    }

    const array = map.get(key);
    if (array === undefined) {
      map.set(key, [value]);
    } else {
      array.push(value);
    }
  }

  return map;
};

/**
 * maps an array, keeping only items where the mapper returns a defined value
 * @param array the array to map
 * @param mapper function to transform each item
 * @returns a new array of defined mapped values
 */
export const mapDefined = <T, R>(
  array: readonly T[],
  mapper: (value: T, index: number) => R | undefined
): R[] => {
  const mapped: R[] = [];

  for (let idx = 0, len = array.length; idx < len; idx++) {
    const result = mapper(array[idx], idx);
    if (result !== undefined) {
      mapped.push(result);
    }
  }

  return mapped;
};

type FalsyValue = false | null | undefined | 0 | "";

/**
 * filters out all falsy values from an array
 * @param array the array to filter
 * @returns a new array with only truthy values
 */
export const definite = <T>(
  array: (T | FalsyValue)[]
): T extends FalsyValue ? never[] : T[] => {
  return array.filter(Boolean) as any;
};

/**
 * returns an array with elements unique by a selector's return value
 * @param array the array to process
 * @param selector function to derive uniqueness keys
 * @returns a new array with elements unique based on the selector's key
 */
export const uniqueBy = <T, K>(
  array: T[],
  selector: (value: T, index: number) => K
): T[] => {
  const keys = new Set<K>();
  const values: T[] = [];

  for (let idx = 0, len = array.length; idx < len; idx++) {
    const value = array[idx];
    const key = selector(value, idx);

    if (keys.has(key)) {
      continue;
    }

    keys.add(key);
    values.push(value);
  }

  return values;
};

/**
 * grabs a random sample of items
 * @param arr array of items to sample from
 * @param n amount of items to sample
 * @returns sample
 */
export const sample = <T>(arr: T[], n: number): T[] => {
  const result = [...arr];
  const len = result.length;

  n = Math.min(n, len);

  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(Math.random() * (len - i));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return len > n ? result.slice(0, Math.max(0, n)) : result;
};

/**
 * grabs a single random item from an array
 * @param arr array of items to sample from
 * @returns a random item, or undefined if the array is empty
 */
export const sampleOne = <T>(arr: T[]): T | undefined => {
  if (arr.length === 0) {
    return undefined;
  }
  return arr[Math.floor(Math.random() * arr.length)];
};
